import * as React from 'react';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
type Variant = 'primary' | 'ghost' | 'solid';
type Size = 'small' | 'medium' | 'large' | 'extra-large';

export interface InputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size' | 'prefix' | 'suffix'> {
	label?: string;
	helper?: string;
	error?: string | null;
	name?: string;
	icon?: React.ReactNode;
	prefix?: React.ReactNode;
	suffix?: React.ReactNode;
	variant?: Variant;
	size?: Size;
	showPasswordToggle?: boolean;
	clearable?: boolean;
	debounce?: number; // ms
	validationDebounce?: number; // ms for onValidate while typing
	onValidate?: (value: string) => string | null | Promise<string | null>;
	disabled?: boolean;
	color?: 'white' | 'black';
}

const sizeClasses: Record<Size, string> = {
	small: 'px-2 py-1 text-sm',
	medium: 'px-3 py-2 text-sm',
	large: 'px-3 py-3 text-base',
	'extra-large': 'px-4 py-4 text-lg',
};

const baseInputClasses =
	'w-full border border-gray-300 rounded text-black  placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500';

function cn(...parts: Array<string | false | null | undefined>) {
	return parts.filter(Boolean).join(' ');
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(function Input(
	props,
	ref,
) {
	const {
		label,
		helper,
		error: errorProp,
		icon,
		prefix,
		suffix,
		variant = 'primary',
		size = 'medium',
		showPasswordToggle = true,
		clearable = true,
		debounce = 200,
		onValidate,
		className,
		value: valueProp,
		defaultValue,
		onChange,   
		type = 'text',
		id,
		name,
		disabled,
		color = 'black',
		...rest
	} = props;

	const isControlled = valueProp !== undefined;
	const [internalValue, setInternalValue] = React.useState<string>(
		(valueProp as string) ?? (defaultValue as string) ?? ''
	);
	const [showPassword, setShowPassword] = React.useState(false);
	const [error, setError] = React.useState<string | null>(errorProp ?? null);
	const [validating, setValidating] = React.useState(false);
	const idAttr = id ?? name ?? `input-${Math.random().toString(36).slice(2, 9)}`;

	React.useEffect(() => setError(errorProp ?? null), [errorProp]);

	React.useEffect(() => {
		if (isControlled) setInternalValue((valueProp as string) ?? '');
	}, [valueProp, isControlled]);

	const debouncedTimer = React.useRef<number | undefined>(undefined);
	const validationTimer = React.useRef<number | undefined>(undefined);
	const validationDebounce = (props as any).validationDebounce ?? 300;

	React.useEffect(() => {
		if (!onChange) return;
		if (debounce <= 0) return;
		window.clearTimeout(debouncedTimer.current);
		debouncedTimer.current = window.setTimeout(() => {
			const synthetic = ({ target: { value: internalValue, name } } as unknown) as React.ChangeEvent<HTMLInputElement>;
			onChange(synthetic);
		}, debounce) as unknown as number;

		return () => {
			window.clearTimeout(debouncedTimer.current);
			window.clearTimeout(validationTimer.current);
		};
	}, [internalValue, onChange, debounce, name]);

	const runValidation = React.useCallback(
		async (val: string) => {
			if (!onValidate) return null;
			try {
				setValidating(true);
				const res = await onValidate(val);
				setError(res ?? null);
				return res ?? null;
			} finally {
				setValidating(false);
			}
		},
		[onValidate]
	);

	const handleBlur = async (e: React.FocusEvent<HTMLInputElement>) => {
		if (rest.onBlur) rest.onBlur(e);
		window.clearTimeout(validationTimer.current);
		await runValidation(internalValue);
	};

	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const val = e.target.value;
		setInternalValue(val);
		if (debounce <= 0 && onChange) onChange(e);
		if (error) setError(null);

		if (onValidate) {
			if (validationDebounce <= 0) {
				runValidation(val);
			} else {
				window.clearTimeout(validationTimer.current);
				validationTimer.current = window.setTimeout(() => {
					runValidation(val);
				}, validationDebounce) as unknown as number;
			}
		}
	};



	const inputType = type === 'password' && showPasswordToggle ? (showPassword ? 'text' : 'password') : type;

	const wrapperClasses = cn(
		'relative',
		className
	);

	const finalInputClasses = cn(
		baseInputClasses,
		color === 'white' ? 'text-black bg-white' : 'text-white bg-gray-800',
		sizeClasses[size],
		error ? 'border-red-500 ring-red-200' : 'border-gray-300',
		prefix ? 'pl-10' : '',
		suffix || clearable ? 'pr-10' : '',
		disabled ? 'opacity-50 cursor-not-allowed bg-gray-700' : '',
	);

	return (
		<div className={wrapperClasses}>
			{label && (
				<label htmlFor={idAttr} className={`block text-sm font-medium mb-1 ${color === 'white' ? 'text-black' : 'text-white'}`}>
					{label}
				</label>
			)}

			<div className="relative flex items-center">
				{prefix && <div className="absolute left-0 pl-3 pointer-events-none">{prefix}</div>}

				<input
					id={idAttr}
					name={name}
					disabled={disabled}
					ref={ref}
					className={finalInputClasses}
					aria-invalid={!!error}
					aria-describedby={
						error ? `${idAttr}-error` : helper ? `${idAttr}-helper` : undefined
					}
					value={internalValue}
					onChange={handleChange}
					onBlur={handleBlur}
					type={inputType}
					{...rest}
				/>

				{icon && <div className="absolute left-3 text-gray-300">{icon}</div>}

				<div className="absolute right-2 flex items-center space-x-2">
					{validating && (
						<div className="text-sm text-gray-400">…</div>
					)}

				

					{type === 'password' && showPasswordToggle && (
						<button
							type="button"
							onClick={() => setShowPassword((s) => !s)}
							className="text-gray-300 hover:text-white focus:outline-none"
							aria-label={showPassword ? 'Hide password' : 'Show password'}
						>
							{showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
						</button>
					)}

					{suffix}
				</div>
			</div>

			{helper && !error && (
				<p id={`${idAttr}-helper`} className="mt-1 text-xs text-gray-400">
					{helper}
				</p>
			)}

			{error && (
				<p id={`${idAttr}-error`} className="mt-1 text-xs text-red-400">
					{error}
				</p>
			)}
		</div>
	);
});

export default Input;
